import { HttpHeader } from "./models";
/**
 * Extension/wrapper of the standard Request type to allow attaching metadata that flows
 * between RequestInterceptors and ResponseInterceptors, and also has affordances (see fromRequest)
 * to ensure Requests are cloned in a cross-env compatible fashion.
 */
export class InterceptedRequest extends Request {
    constructor(url, init, metadata = {}) {
        super(url, init);
        this.metadata = metadata;
    }
}
/**
 * Clones a request and any of its metadata, while allowing for inserting additional headers
 * or updating the URL.
 * @param request The request to clone
 * @param newData Properties to configure the cloned Request
 * @param newData.init Standard Request initializer; what you would pass to new Request(baseRequest, requestInit)
 * @param newData.metadata InterceptedRequest metadata to specify for the new request
 * @param newData.additionalHeaders Headers to insert into the cloned Request
 * @param newData.url New URL to use for the cloned Request
 */
InterceptedRequest.fromRequest = async (request, newData = {}) => {
    var _a, _b;
    if (request.bodyUsed) {
        throw new Error("Cannot copy the Request as its body has been consumed already");
    }
    const { body: requestInitBody, headers: requestInitHeaders, ...requestInit } = (_a = newData.init) !== null && _a !== void 0 ? _a : {};
    // Destructure properties out of the original request, while allowing for overrides
    // Note that since Request is class, these are non-enumerable
    // and we can't just {...spread } the Request object
    const { cache, credentials, headers, integrity, keepalive, method, mode, redirect, referrer, referrerPolicy, signal, } = request;
    const newHeaders = new Headers(requestInitHeaders !== null && requestInitHeaders !== void 0 ? requestInitHeaders : headers);
    if (newData.additionalHeaders) {
        const additionalHeaders = new Headers(newData.additionalHeaders);
        for (const [name, value] of additionalHeaders) {
            newHeaders.set(name, value);
        }
    }
    // Create a fresh request init object
    const newRequestInit = {
        cache,
        credentials,
        headers: newHeaders,
        integrity,
        keepalive,
        method,
        mode,
        redirect,
        referrer,
        referrerPolicy,
        signal,
        // Spread requestInit to override everything that's overrideable
        // and doesn't get special treatment (body, headers)
        ...requestInit,
    };
    // If the original request has a body, we will need to do some additional work
    // to copy it into a new request while still using a new URL for the copied request.
    // https://stackoverflow.com/a/34641566
    // https://stackoverflow.com/a/48713509
    if (requestInitBody) {
        // If the client specified a new body, we can just use that directly
        newRequestInit.body = requestInitBody;
    }
    // Note: this "_bodyInit" check is to cover the whatwg-fetch polyfill used by React Native,
    // which does not provide another way to check for the existence of a body and has some quirks
    // with how it handles copying Requests
    else if ("_bodyInit" in request) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newRequestInit.body = request._bodyInit;
    }
    // If we're using a different polyfill (or real, native fetch), we can check to see if the
    // request.body property is implemented or if a Content-Type header has been applied to the request.
    // Note that fetch will automatically apply Content-Type for most bodies if the user didn't provide
    // their own, so this is a pretty safe way to query for a body.
    else if (request.body || request.headers.has(HttpHeader.ContentType)) {
        const bodyBlob = await request.blob();
        if (bodyBlob.size > 0) {
            newRequestInit.body = bodyBlob;
        }
    }
    const oldMetadata = request instanceof InterceptedRequest ? request.metadata : {};
    const newRequest = new InterceptedRequest((_b = newData.url) !== null && _b !== void 0 ? _b : request.url, newRequestInit, {
        ...oldMetadata,
        ...newData.metadata,
    });
    return newRequest;
};
//# sourceMappingURL=InterceptedRequest.js.map